import * as React from 'react';

import { Tooltip, type TooltipPositioningProps, TooltipShell } from './tooltip';

/**
 * Props the HOC consumes. Everything else is forwarded to the wrapped component.
 *
 * The tooltip half lives in `TooltipPositioningProps`, shared with `DisabledTooltip`
 * so the two cannot drift. The three names below are NOT prefixed: they are
 * inherited from the legacy HOC, and renaming them would break the import-only
 * migration. That means a wrapped component declaring `tooltipContent` or
 * `children` loses it — the prefixed positioning props exist so the same hazard does
 * not extend to `side` and `align`.
 */
export interface WithTooltipProps extends TooltipPositioningProps {
  /** Bubble contents. Falsy renders no portal at all (see below). */
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * The element type the wrapped component's own ref points at.
 *
 * `forwardRef` wraps its prop type in `PropsWithoutRef`, which strips `ref` out of
 * `P` and substitutes whatever the first type argument says. Passing `unknown` there
 * would compile `withTooltip(ButtonThatTakesAButtonRef)` against an
 * `HTMLDivElement` ref without complaint — so recover the element type from `P`
 * (a `forwardRef` component's props carry `ref?: Ref<T>`) and hand that to
 * `forwardRef` instead, keeping the check the plain-function version got for free.
 */
type WrappedRef<P> = P extends { ref?: React.Ref<infer R> | undefined } ? R : unknown;

/**
 * withTooltip — wraps a component so it renders inside a registry Tooltip.
 *
 * Drop-in for the legacy HOC at `@/legacy/with-tooltip`, with one deliberate
 * difference: **this mounts no `TooltipProvider`.**
 *
 * Radix's Provider is app-level policy. It owns the hover delay and the
 * skip-delay group that lets a second tooltip open instantly after the first.
 * The legacy HOC mounts one per instance, so every tooltip sits in a group of
 * one and shadows whatever the app configured. Worse, it forwards the caller's
 * `delayDuration` — usually `undefined` — into that Provider, whose own default
 * parameter is 700ms. An app that set `delayDuration={0}` at its root therefore
 * gets 700ms back from every wrapped component, which is why gaia carries ~50
 * separate `delayDuration={0}` props and 7 redundant nested Providers. See
 * UX-3107.
 *
 * **Consumers must render a `TooltipProvider` at the app root.** Radix throws
 * without one. `InputLabelInfo` in the registry Input makes the same
 * requirement for the same reason.
 *
 * `delayDuration` on this component overrides the provider for this tooltip only:
 * Radix resolves the root as `delayDurationProp ?? providerContext.delayDuration`
 * (verified against `@radix-ui/react-tooltip` 1.2.7). So a per-instance override is
 * still available — it just no longer shadows the app's configuration for everything
 * else. Full precedence table in `AGENTS.md`, kept in one place so a Radix bump has
 * one thing to re-check rather than four.
 *
 * @example
 * ```tsx
 * // Once, at the app root:
 * <TooltipProvider delayDuration={0}>{app}</TooltipProvider>
 *
 * // Anywhere:
 * const ButtonWithTooltip = withTooltip(Button);
 * <ButtonWithTooltip tooltipContent="Save changes" tooltipSide="bottom" tooltipAlign="start">
 *   Save
 * </ButtonWithTooltip>
 * ```
 *
 * @remarks
 * Three things this cannot enforce in types, in rough order of how much they hurt:
 *
 * 1. **Call it at module scope, never inside render.** `withTooltip(X)` returns a new
 *    component type on every call, so calling it during render makes React unmount
 *    and remount the whole subtree each time — losing focus, state and any open
 *    tooltip. Assign it once: `const ButtonWithTooltip = withTooltip(Button)`.
 * 2. **The wrapped component must forward its ref to a DOM element and spread the
 *    rest.** Radix's `asChild` merges the trigger's props and ref onto it; a plain
 *    function component silently drops them, and the tooltip then fails to anchor.
 * 3. **The prop names above are consumed, not forwarded.** The positioning props are
 *    prefixed so they cannot collide, but `tooltipContent`, `contentClassName` and
 *    `delayDuration` are inherited from the legacy HOC and are not — wrapping a
 *    component that declares one of those means the HOC eats it. Compose `Tooltip` /
 *    `TooltipTrigger` / `TooltipContent` directly in that case.
 *
 * The returned component forwards its ref to the wrapped component, so a caller can
 * measure or focus the underlying element — `withTooltip(Span)` with a ref is how a
 * truncation-gated tooltip measures its own overflow. Radix composes that ref with
 * the one `asChild` needs for anchoring, so both survive.
 *
 * **Gating a tooltip on runtime state: pass `tooltipContent={undefined}`.** There is
 * deliberately no `disabled` prop. The trigger is rendered outside the branch that
 * toggles the portal, so turning content on and off keeps the wrapped element's DOM
 * node identity — no remount, and effects like a measuring `ResizeObserver` survive.
 * A HOC that early-returns a bare component when content is falsy does not have that
 * property, and needs a separate flag to work around it; this one does not.
 */
export const withTooltip = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
  const Wrapped = WrappedComponent as React.ComponentType<P & React.RefAttributes<unknown>>;

  const WithTooltip = React.forwardRef<WrappedRef<P>, P & WithTooltipProps>(
    (
      {
        contentClassName,
        delayDuration,
        tooltipContent,
        tooltipSide,
        tooltipAlign,
        tooltipSideOffset,
        tooltipAlignOffset,
        tooltipArrow,
        interactiveContent,
        ...props
      },
      ref
    ) => (
      // `delayDuration` on the root, not on a Provider we mount. Radix reads
      // `prop ?? providerContext.delayDuration`, so `undefined` inherits whatever the
      // app's provider configured instead of resetting it to that Provider's default.
      //
      <Tooltip delayDuration={delayDuration}>
        <TooltipShell
          content={tooltipContent}
          trigger={<Wrapped ref={ref} {...(props as P)} />}
          contentClassName={contentClassName}
          tooltipSide={tooltipSide}
          tooltipAlign={tooltipAlign}
          tooltipSideOffset={tooltipSideOffset}
          tooltipAlignOffset={tooltipAlignOffset}
          tooltipArrow={tooltipArrow}
          interactiveContent={interactiveContent}
        />
      </Tooltip>
    )
  );

  WithTooltip.displayName = `withTooltip(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithTooltip;
};
